import type { BlockDefinition, BindingSpec, InspectorField, LicenseTier } from "./block-definition.js";

export interface RegistryOptions {
  /** Licence tiers that are enabled. Blocks above the enabled tier are still rendered but flagged. */
  enabledTiers?: LicenseTier[];
}

const TIER_RANK: Record<LicenseTier, number> = { community: 0, pro: 1, enterprise: 2 };

/**
 * The block registry. Built-in blocks and host custom blocks are registered the
 * same way; there is no privileged path.
 */
export class BlockRegistry {
  private readonly defs = new Map<string, BlockDefinition<any>>();
  private readonly listeners = new Set<() => void>();
  private readonly enabledTiers: Set<LicenseTier>;

  constructor(defs: Array<BlockDefinition<any>> = [], opts: RegistryOptions = {}) {
    this.enabledTiers = new Set(opts.enabledTiers ?? ["community", "pro", "enterprise"]);
    for (const d of defs) this.register(d);
  }

  register<P extends Record<string, unknown>>(def: BlockDefinition<P>): this {
    if (this.defs.has(def.type)) throw new Error(`Block type "${def.type}" is already registered`);
    this.defs.set(def.type, def);
    this.emit();
    return this;
  }

  /** Replaces an existing definition (used for hot-swapping custom blocks in dev). */
  replace<P extends Record<string, unknown>>(def: BlockDefinition<P>): this {
    this.defs.set(def.type, def);
    this.emit();
    return this;
  }

  unregister(type: string): this {
    this.defs.delete(type);
    this.emit();
    return this;
  }

  get(type: string): BlockDefinition | undefined {
    return this.defs.get(type);
  }

  has(type: string): boolean {
    return this.defs.has(type);
  }

  require(type: string): BlockDefinition {
    const d = this.defs.get(type);
    if (!d) throw new Error(`Unknown block type "${type}"`);
    return d;
  }

  list(): BlockDefinition[] {
    return [...this.defs.values()];
  }

  /** Blocks shown in the palette, grouped by category, honouring `hidden`. */
  palette(): BlockDefinition[] {
    return this.list().filter((d) => !d.hidden && d.category !== "structure");
  }

  isTierEnabled(tier: LicenseTier | undefined): boolean {
    return this.enabledTiers.has(tier ?? "community");
  }

  /** The highest tier required by a set of block types. */
  requiredTier(types: Iterable<string>): LicenseTier {
    let best: LicenseTier = "community";
    for (const t of types) {
      const tier = this.defs.get(t)?.license ?? "community";
      if (TIER_RANK[tier] > TIER_RANK[best]) best = tier;
    }
    return best;
  }

  /** Effective binding specs for a block: explicit `bindings`, else derived from the inspector. */
  bindingsOf(type: string): BindingSpec[] {
    const def = this.defs.get(type);
    if (!def) return [];
    return bindingSpecsFor(def);
  }

  /** Whether `childType` may be placed inside `parentType`. */
  accepts(parentType: string, childType: string): boolean {
    const parent = this.defs.get(parentType);
    const child = this.defs.get(childType);
    if (!parent?.container) return false;
    for (const rule of parent.container.accepts) {
      if (rule === childType) return true;
      if (rule === "@blocks" && child && child.category !== "structure") return true;
      if (rule === "@rows" && childType === "row") return true;
      if (rule === "@columns" && childType === "column") return true;
      if (rule === "@sections" && childType === "section") return true;
    }
    return false;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const l of this.listeners) l();
  }
}

const bindingCache = new WeakMap<BlockDefinition<any>, BindingSpec[]>();

export function bindingSpecsFor(def: BlockDefinition<any>): BindingSpec[] {
  const hit = bindingCache.get(def);
  if (hit) return hit;
  let specs: BindingSpec[];
  if (def.bindings) specs = def.bindings;
  else {
    specs = [];
    const visit = (fields: InspectorField[]) => {
      for (const f of fields) {
        switch (f.kind) {
          case "binding":
            specs.push({ prop: f.prop, kind: "value", valueType: f.valueType });
            break;
          case "collection":
            specs.push({ prop: f.prop, kind: "collection" });
            break;
          case "expression":
            specs.push({ prop: f.prop, kind: "value" });
            break;
          case "richtext":
            specs.push({ prop: f.prop, kind: "richtext" });
            break;
          case "condition":
            specs.push({ prop: f.prop, kind: "condition" });
            break;
          case "text":
          case "textarea":
            if (f.allowTokens !== false) specs.push({ prop: f.prop, kind: "template" });
            break;
          case "image":
            if (f.allowBinding !== false) specs.push({ prop: f.prop, kind: "template" });
            break;
          case "list":
            visit(f.fields);
            break;
          default:
            break;
        }
      }
    };
    for (const s of def.inspector ?? []) visit(s.fields);
  }
  bindingCache.set(def, specs);
  return specs;
}
